import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Users, DollarSign, Activity, TrendingUp, Shield, LogOut, RefreshCw, 
  Search, Brain, MessageSquare, UserX, UserPlus, Trash2, Eye, PlayCircle,
  Download, AlertCircle, CheckCircle, XCircle, Globe, Calendar, Filter,
  BarChart3, TrendingDown, Zap, Target, Database
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];

const SuperAdminNew = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [deepAnalytics, setDeepAnalytics] = useState(null);
  
  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all'); // all, users, analyses, content
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  
  // Filtered data
  const [searchResults, setSearchResults] = useState({ users: [], analyses: [] });

  useEffect(() => {
    const isAuth = sessionStorage.getItem('superadmin_auth') === 'true';
    if (isAuth) {
      setAuthenticated(true);
      loadAllData();
    }
  }, []);

  useEffect(() => {
    performSearch();
  }, [searchQuery, searchType, users, analyses, dateFilter, contentTypeFilter]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'SuperAdmin2025!' && secretKey === 'emoq_super_secret_key') {
      sessionStorage.setItem('superadmin_auth', 'true');
      setAuthenticated(true);
      toast.success('Super admin access granted');
      loadAllData();
    } else {
      toast.error('Invalid credentials');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('superadmin_auth');
    setAuthenticated(false);
    setPassword('');
    setSecretKey('');
    toast.info('Logged out successfully');
  };

  const loadAllData = async () => {
    setLoading(true);
    console.log('🔄 Loading all super admin data...');
    
    try {
      const [statsRes, usersRes, analysesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/analyses`)
      ]);
      
      console.log('✅ Stats:', statsRes.data);
      console.log('✅ Users:', usersRes.data.users?.length);
      console.log('✅ Analyses:', analysesRes.data.analyses?.length);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setAnalyses(analysesRes.data.analyses || []);
      
      // Build deep analytics from the data we have
      const buildDeepAnalytics = (stats, analyses) => {
        const contentByType = {};
        const emotionCounts = {};
        
        analyses.forEach(a => {
          const type = a.content_type || 'unknown';
          contentByType[type] = (contentByType[type] || 0) + 1;
          
          const emotion = a.dominant_emotion;
          if (emotion) {
            emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
          }
        });
        
        return {
          content_analysis: {
            by_type: contentByType
          },
          emotion_trends: {
            top_emotions: emotionCounts
          }
        };
      };
      
      setDeepAnalytics(buildDeepAnalytics(statsRes.data, analysesRes.data.analyses || []));
      
      toast.success('Data loaded successfully');
    } catch (error) {
      console.error('❌ Failed to load data:', error);
      toast.error('Failed to load data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const performSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults({ users: users.slice(0, 20), analyses: analyses.slice(0, 20) });
      return;
    }

    const query = searchQuery.toLowerCase();
    let filteredUsers = [];
    let filteredAnalyses = [];

    // Filter by date
    const getFilteredByDate = (items, dateField) => {
      if (dateFilter === 'all') return items;
      
      const now = new Date();
      const filterDate = dateFilter === 'today' 
        ? new Date(now.setHours(0,0,0,0))
        : dateFilter === 'week'
        ? new Date(now.setDate(now.getDate() - 7))
        : new Date(now.setDate(now.getDate() - 30));
      
      return items.filter(item => {
        const itemDate = new Date(item[dateField]);
        return itemDate >= filterDate;
      });
    };

    // Search users
    if (searchType === 'all' || searchType === 'users') {
      filteredUsers = users.filter(user =>
        (user.email && user.email.toLowerCase().includes(query)) ||
        (user.name && user.name.toLowerCase().includes(query)) ||
        (user.id && user.id.toLowerCase().includes(query))
      );
      filteredUsers = getFilteredByDate(filteredUsers, 'created_at');
    }

    // Search analyses
    if (searchType === 'all' || searchType === 'analyses' || searchType === 'content') {
      filteredAnalyses = analyses.filter(analysis => {
        const matchesQuery = 
          (analysis.content && analysis.content.toLowerCase().includes(query)) ||
          (analysis.custom_question && analysis.custom_question.toLowerCase().includes(query)) ||
          (analysis.custom_answer && analysis.custom_answer.toLowerCase().includes(query)) ||
          (analysis.dominant_emotion && analysis.dominant_emotion.toLowerCase().includes(query)) ||
          (analysis.content_type && analysis.content_type.toLowerCase().includes(query)) ||
          (analysis.user_id && analysis.user_id.toLowerCase().includes(query));
        
        const matchesContentType = contentTypeFilter === 'all' || analysis.content_type === contentTypeFilter;
        
        return matchesQuery && matchesContentType;
      });
      filteredAnalyses = getFilteredByDate(filteredAnalyses, 'timestamp');
    }

    setSearchResults({ 
      users: filteredUsers.slice(0, 50), 
      analyses: filteredAnalyses.slice(0, 50) 
    });
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted successfully');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBlockUser = async (userId, block = true) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/block`, { blocked: block });
      toast.success(block ? 'User blocked successfully' : 'User unblocked successfully');
      loadAllData();
    } catch (error) {
      toast.error('Failed to update user status: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-12 h-12 text-purple-600" />
            </div>
            <CardTitle className="text-2xl text-center">Super Admin Access</CardTitle>
            <p className="text-sm text-center text-gray-500">Business Intelligence Dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  placeholder="Enter password"
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secretKey">Secret Key</Label>
                <Input 
                  id="secretKey" 
                  type="password" 
                  value={secretKey} 
                  onChange={(e) => setSecretKey(e.target.value)} 
                  placeholder="Enter secret key"
                  required 
                />
              </div>
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
                Access Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate knowledge bank insights
  const knowledgeInsights = calculateKnowledgeInsights(analyses, users);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  emoQ.ai Super Admin
                </h1>
                <p className="text-xs text-gray-500">Global Insights & Management</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={loadAllData} 
                disabled={loading}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Key Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Users"
            value={stats?.total_users || 0}
            icon={Users}
            trend={`+${stats?.new_users_week || 0} this week`}
            color="purple"
          />
          <MetricCard
            title="Total Analyses"
            value={stats?.total_analyses || 0}
            icon={Activity}
            trend={`${analyses.length} loaded`}
            color="blue"
          />
          <MetricCard
            title="Revenue"
            value={`$${(stats?.total_revenue || 0).toFixed(2)}`}
            icon={DollarSign}
            trend={`${stats?.active_subscriptions || 0} active subs`}
            color="green"
          />
          <MetricCard
            title="Knowledge Bank"
            value={knowledgeInsights.totalUniqueTopics}
            icon={Brain}
            trend={`${knowledgeInsights.totalQuestions} questions`}
            color="indigo"
          />
        </div>

        {/* Advanced Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Global Search & Filters
            </CardTitle>
            <CardDescription>
              Search across users, analyses, content, questions, and responses
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search anything: emails, content, questions, emotions, user IDs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Advanced
              </Button>
            </div>
            
            <div className="flex gap-4 flex-wrap">
              <div className="flex gap-2">
                <Badge 
                  variant={searchType === 'all' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSearchType('all')}
                >
                  All
                </Badge>
                <Badge 
                  variant={searchType === 'users' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSearchType('users')}
                >
                  Users
                </Badge>
                <Badge 
                  variant={searchType === 'analyses' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSearchType('analyses')}
                >
                  Analyses
                </Badge>
                <Badge 
                  variant={searchType === 'content' ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSearchType('content')}
                >
                  Content & Questions
                </Badge>
              </div>
              
              <div className="flex gap-2">
                {['all', 'today', 'week', 'month'].map(filter => (
                  <Badge 
                    key={filter}
                    variant={dateFilter === filter ? 'default' : 'outline'}
                    className="cursor-pointer capitalize"
                    onClick={() => setDateFilter(filter)}
                  >
                    <Calendar className="w-3 h-3 mr-1" />
                    {filter}
                  </Badge>
                ))}
              </div>
              
              <div className="flex gap-2">
                {['all', 'text', 'image', 'video', 'url'].map(type => (
                  <Badge 
                    key={type}
                    variant={contentTypeFilter === type ? 'default' : 'outline'}
                    className="cursor-pointer capitalize"
                    onClick={() => setContentTypeFilter(type)}
                  >
                    {type}
                  </Badge>
                ))}
              </div>
            </div>

            {searchQuery && (
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">
                  Found: {searchResults.users.length} users, {searchResults.analyses.length} analyses
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="knowledge">Knowledge Bank</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="search">Search Results</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <OverviewDashboard 
              stats={stats}
              deepAnalytics={deepAnalytics}
              analyses={analyses}
              users={users}
            />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <UserManagement 
              users={searchQuery ? searchResults.users : users.slice(0, 50)}
              onDelete={handleDeleteUser}
              onBlock={handleBlockUser}
              onSelect={setSelectedUser}
            />
          </TabsContent>

          {/* Knowledge Bank Tab */}
          <TabsContent value="knowledge" className="space-y-4">
            <KnowledgeBank 
              analyses={analyses}
              insights={knowledgeInsights}
            />
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-4">
            <TrendsAnalysis 
              analyses={analyses}
              deepAnalytics={deepAnalytics}
            />
          </TabsContent>

          {/* Search Results Tab */}
          <TabsContent value="search" className="space-y-4">
            <SearchResults 
              results={searchResults}
              onSelectUser={setSelectedUser}
              onSelectAnalysis={setSelectedAnalysis}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
      {selectedAnalysis && (
        <AnalysisDetailModal analysis={selectedAnalysis} onClose={() => setSelectedAnalysis(null)} />
      )}
    </div>
  );
};

// Helper Components
const MetricCard = ({ title, value, icon: Icon, trend, color }) => {
  const colorClasses = {
    purple: 'from-purple-500 to-purple-600',
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    indigo: 'from-indigo-500 to-indigo-600'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <Icon className={`w-5 h-5 bg-gradient-to-r ${colorClasses[color]} text-white p-1 rounded`} />
        </div>
        <p className="text-3xl font-bold">{value}</p>
        <p className="text-xs text-gray-500 mt-1">{trend}</p>
      </CardContent>
    </Card>
  );
};

const OverviewDashboard = ({ stats, deepAnalytics, analyses, users }) => {
  const contentTypeData = deepAnalytics?.content_analysis?.by_type 
    ? Object.entries(deepAnalytics.content_analysis.by_type).map(([name, value]) => ({ name, value }))
    : [];

  const emotionData = deepAnalytics?.emotion_trends?.top_emotions
    ? Object.entries(deepAnalytics.emotion_trends.top_emotions).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Content Type Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={contentTypeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {contentTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top Emotions Analyzed</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={emotionData.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

const UserManagement = ({ users, onDelete, onBlock, onSelect }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          User Management ({users.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {users.map((user) => (
            <div 
              key={user.id} 
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelect(user)}
            >
              <div className="flex-1">
                <p className="font-medium">{user.email}</p>
                <p className="text-sm text-gray-500">{user.name || 'No name'}</p>
                <p className="text-xs text-gray-400">
                  Joined: {new Date(user.created_at).toLocaleDateString()} | 
                  Credits: {user.credits || 0}
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); onSelect(user); }}
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); onBlock(user.id, !user.blocked); }}
                >
                  <UserX className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={(e) => { e.stopPropagation(); onDelete(user.id); }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const KnowledgeBank = ({ analyses, insights }) => {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5" />
            Global Knowledge Bank
          </CardTitle>
          <CardDescription>
            Insights derived from {analyses.length} analyses across the world
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm font-medium text-purple-900">Total Questions Asked</p>
              <p className="text-2xl font-bold text-purple-600">{insights.totalQuestions}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-900">Unique Topics</p>
              <p className="text-2xl font-bold text-blue-600">{insights.totalUniqueTopics}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-green-900">Enhanced Content</p>
              <p className="text-2xl font-bold text-green-600">{insights.enhancedCount}</p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold">Top Questions & Topics</h3>
            {insights.topQuestions.slice(0, 10).map((item, idx) => (
              <div key={idx} className="p-3 border rounded-lg">
                <p className="text-sm font-medium text-gray-900">{item.question}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Asked {item.count} times | Category: {item.contentType}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const TrendsAnalysis = ({ analyses, deepAnalytics }) => {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Global Trends & Patterns</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">Trend analysis coming soon with more data...</p>
        </CardContent>
      </Card>
    </div>
  );
};

const SearchResults = ({ results, onSelectUser, onSelectAnalysis }) => {
  return (
    <div className="space-y-4">
      {results.users.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Users ({results.users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {results.users.map(user => (
                <div 
                  key={user.id}
                  className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                  onClick={() => onSelectUser(user)}
                >
                  <p className="font-medium">{user.email}</p>
                  <p className="text-sm text-gray-500">{user.name}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {results.analyses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Analyses ({results.analyses.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {results.analyses.map(analysis => (
                <div 
                  key={analysis.id}
                  className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                  onClick={() => onSelectAnalysis(analysis)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge>{analysis.content_type}</Badge>
                    <span className="text-xs text-gray-500">
                      {new Date(analysis.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm line-clamp-2">{analysis.content || analysis.content_preview}</p>
                  {analysis.custom_question && (
                    <p className="text-xs text-purple-600 mt-1">Q: {analysis.custom_question}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {results.users.length === 0 && results.analyses.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No results found. Try a different search term.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const UserDetailModal = ({ user, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>User Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Email</Label>
            <p className="font-medium">{user.email}</p>
          </div>
          <div>
            <Label>Name</Label>
            <p>{user.name || 'Not provided'}</p>
          </div>
          <div>
            <Label>User ID</Label>
            <p className="text-sm font-mono">{user.id}</p>
          </div>
          <div>
            <Label>Credits</Label>
            <p>{user.credits || 0}</p>
          </div>
          <div>
            <Label>Created</Label>
            <p>{new Date(user.created_at).toLocaleString()}</p>
          </div>
          <Button onClick={onClose} className="w-full">Close</Button>
        </CardContent>
      </Card>
    </div>
  );
};

const AnalysisDetailModal = ({ analysis, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Analysis Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Type</Label>
            <Badge>{analysis.content_type}</Badge>
          </div>
          <div>
            <Label>Content</Label>
            <p className="text-sm">{analysis.content || analysis.content_preview}</p>
          </div>
          {analysis.custom_question && (
            <div>
              <Label>User Question</Label>
              <p className="text-sm">{analysis.custom_question}</p>
            </div>
          )}
          {analysis.custom_answer && (
            <div>
              <Label>AI Answer</Label>
              <p className="text-sm">{analysis.custom_answer}</p>
            </div>
          )}
          <div>
            <Label>Dominant Emotion</Label>
            <Badge>{analysis.dominant_emotion}</Badge>
          </div>
          <div>
            <Label>Timestamp</Label>
            <p className="text-sm">{new Date(analysis.timestamp).toLocaleString()}</p>
          </div>
          <Button onClick={onClose} className="w-full">Close</Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper function to calculate knowledge insights
const calculateKnowledgeInsights = (analyses, users) => {
  const questionsMap = new Map();
  let enhancedCount = 0;

  analyses.forEach(analysis => {
    // Count enhanced content
    if (analysis.enhancement || analysis.super_enhancement_creative || 
        analysis.super_enhancement_photo || analysis.super_enhancement_text_tone ||
        analysis.super_enhancement_video_captions) {
      enhancedCount++;
    }

    // Track questions
    if (analysis.custom_question) {
      const key = analysis.custom_question.toLowerCase();
      if (!questionsMap.has(key)) {
        questionsMap.set(key, {
          question: analysis.custom_question,
          count: 0,
          contentType: analysis.content_type
        });
      }
      questionsMap.get(key).count++;
    }
  });

  const topQuestions = Array.from(questionsMap.values())
    .sort((a, b) => b.count - a.count);

  return {
    totalQuestions: questionsMap.size,
    totalUniqueTopics: questionsMap.size,
    enhancedCount,
    topQuestions
  };
};

export default SuperAdminNew;
